import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';

const debounce = (func, wait) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, args), wait);
	};
};

/**
 * AsyncAutocomplete component
 *
 * This component is a wrapper around the MUI Autocomplete component and provides the same functionality
 *  but with additional builtin features to support server side pagination and search/filtering.
 *   - server side pagination, load more options on scroll
 *   - server side search/filtering
 *   - builtin loading state
 *
 * @reference https://mui.com/material-ui/react-autocomplete/#api
 *
 * @note There was a bug in the older version of Mui where the listbox was reset to the top when new options were loaded.
 *  This was fixed in Mui v5.11.7, https://github.com/mui/material-ui/pull/35735, though this was developed and tested
 *  to work on the latest @mui/material 5.16.12+.
 *
 * @example
 * <AsyncAutocomplete
 *   label='Test Autocomplete'
 *   options={data?.data}
 *   selectedOption={selectedOption}
 *   totalOptions={data?.total}
 *   loading={isLoading}
 *   onUpdateQuery={handleUpdateQuery}
 *   onChange={handleChange}
 *   {...additionalAutocompleteProps}
 * />
 */
const AsyncAutocomplete = ({
	label = '',
	options = [],
	selectedOption,
	totalOptions,
	loading = false,
	threshold = 250,
	onUpdateQuery,
	onChange,
	renderInputProps = {},
	...props
}) => {
	const [currOptions, setCurrOptions] = useState([]);
	const [currPage, setCurrentPage] = useState(1);
	const [searchValue, setSearchValue] = useState('');

	useEffect(() => {
		if (options.length > 0) {
			setCurrOptions(prevOptions => {
				if (prevOptions.length === 0) {
					return options;
				}
				return [...prevOptions, ...options];
			});
		}
	}, [options]);

	const handleScroll = event => {
		// if all options are loaded or currently loading more, do not attempt to load again
		if (currOptions.length >= totalOptions || loading) return;

		const listboxNode = event.currentTarget;
		if (
			listboxNode.scrollTop + listboxNode.clientHeight >=
			listboxNode.scrollHeight - threshold
		) {
			setCurrentPage(currPage + 1);
		}
	};

	/* debounce input search */
	const handleDebouncedSearch = debounce(query => {
		if (selectedOption !== null) {
			onChange(null);
		}

		if (currPage > 1) {
			setCurrentPage(1);
		}

		if (currOptions.length > 0) {
			setCurrOptions([]);
		}

		setSearchValue(query);
	}, 300);

	const handleClear = () => {
		onChange(null);
		setCurrOptions([]);
		setSearchValue(null);
		setCurrentPage(1);
	};

	useEffect(() => {
		onUpdateQuery(searchValue, currPage);
	}, [searchValue, currPage]);

	return (
		<Autocomplete
			{...props}
			options={currOptions}
			loading={loading}
			filterOptions={x => x}
			ListboxProps={{
				sx: { maxHeight: 316 },
				onScroll: handleScroll,
			}}
			onInputChange={(_e, newInputValue, reason) => {
				if (reason === 'input') {
					handleDebouncedSearch(newInputValue);
				}
			}}
			onBlur={() => {
				setSearchValue('');
			}}
			onChange={(e, newValue, reason) => {
				if (reason === 'clear') {
					handleClear();
				} else if (reason === 'selectOption') {
					setSearchValue(newValue);
					setCurrentPage(1);
					onChange(newValue);
				}
			}}
			renderInput={params => (
				<TextField
					{...params}
					{...renderInputProps}
					label={label}
					disabled={loading || params.disabled}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? (
									<CircularProgress
										title={`Loading ${label} data...`}
										color='primary'
										size={24}
									/>
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};

AsyncAutocomplete.propTypes = {
	/**
	 * label for the Autocomplete
	 */
	label: PropTypes.string,

	/**
	 * loading state
	 */
	loading: PropTypes.bool,

	/**
	 * array of options to display in the dropdown
	 *
	 * note: the master array is stored in this component so the
	 *  parent component only needs to send in new/additional options
	 */
	options: PropTypes.array,

	/**
	 * object representing the selected option
	 */
	selectedOption: PropTypes.object,

	/**
	 * total number of options available in the database/filtered result set
	 */
	totalOptions: PropTypes.number.isRequired,

	/**
	 * callback function to handle scroll or search event
	 */
	onUpdateQuery: PropTypes.func.isRequired,

	/**
	 * callback function to handle change
	 * @param {object} newValue - selected option
	 */
	onChange: PropTypes.func.isRequired,

	/**
	 * threshold to trigger loading more options when scrolling
	 * @default 250
	 */
	threshold: PropTypes.number,

	/**
	 * additional props to pass to the TextField being rendered via the `renderInput`
	 * @link https://mui.com/material-ui/api/text-field/
	 */
	renderInputProps: PropTypes.object,
};

export default AsyncAutocomplete;
